import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { isTeacherSide, maskRoute, type FeedbackContext } from "@edu/shared";
import { useSession } from "../../lib/session.tsx";
import { snapshot } from "./context.ts";

/**
 * Провайдер заметок о неточностях: одно окно на всё приложение и одна точка
 * сборки снимка (раздел 6.2 `docs/feedback-notes-design.md`).
 *
 * **Зачем контекст вообще.** Точки входа живут в трёх разных поддеревьях:
 * пунктом в подвале сайдбара (`Shell`), строкой в меню шестерёнки комнаты и
 * строкой в уведомлении «без видео» (`LessonRoom`), кнопкой в углу архива. А
 * данные снимка приходят ещё из двух мест. Без общего контекста механизм был бы
 * изобретён по месту трижды и разошёлся бы к третьему разу.
 *
 * ⚠️ **Поставщиков именно два, и это не усложнение.** `role`, `video` и
 * `noVideoReason` это props `LessonRoom`, а `Y.Doc` и счётчики Excalidraw лежат
 * в локальном состоянии `Whiteboard`, то есть в соседнем поддереве: комната до
 * доски не дотягивается. Снимок собирается слиянием того, что зарегистрировано.
 *
 * ⚠️ **Zustand здесь не берём**, хотя 5.1 CLAUDE.md назначает его состоянию UI.
 * Его нет ни в `package.json`, ни в одной строке `apps/web/src`, то есть это
 * была бы первая зависимость в проекте, заведённая ради одного виджета. Место
 * провайдера в дереве (`AppRoutes`) выше обоих поддеревьев, и React-контекста
 * достаточно.
 *
 * ⚠️ **Значение контекста стабильно, а поставщики лежат в `useRef`.** Иначе
 * регистрация из комнаты перерисовывала бы всё дерево маршрутов при каждом
 * монтировании, а монтируется комната на каждом заходе на урок.
 *
 * ⚠️ **`context.ts` импортируется значением, то есть едет в стартовый чанк
 * вместе с провайдером**, хотя таблица раздела 6.1 проекта числит его ленивым.
 * Это следствие более позднего решения того же проекта (6.6): снимок снимается в
 * момент ОТКРЫТИЯ окна, а открывает окно провайдер, и тянуть ради этого чанк
 * через `await` значило бы снимать снимок на такт позже события, про которое
 * пишут. Цена мала и посчитана: сборщик снимка это несколько десятков строк без
 * единой зависимости, тогда как ленивым остаётся то, что тяжело, само окно.
 */

export interface FeedbackApi {
  /**
   * Показывать ли точку входа. Правда означает сразу три вещи: человек вошёл,
   * он сторона преподавателя, и ленивый чанк окна УЖЕ ПРИЕХАЛ. Последнее часть
   * условия намеренно: точка входа, которая на нажатие пытается скачать чанк по
   * мёртвой сети, обманывает ровно того, кто нажал её из-за мёртвой сети.
   */
  enabled: boolean;
  open(): void;
  /** Ключ, потому что поставщиков два: комната и доска. */
  provide(key: "room" | "board", fn: (() => Partial<FeedbackContext>) | null): void;
}

const Ctx = createContext<FeedbackApi | null>(null);

/** Что открыто прямо сейчас. Снимок снят при открытии и больше не меняется. */
interface Opened {
  snapshot: FeedbackContext;
  route: string;
}

type WidgetModule = typeof import("./FeedbackWidget.tsx");
type Widget = WidgetModule["FeedbackWidget"];

export function FeedbackProvider(props: { children: React.ReactNode }): React.ReactElement {
  const { state } = useSession();
  const userId = state.status === "signed" ? state.me.userId : null;
  /**
   * Сторона преподавателя считается уже написанной функцией из общего пакета.
   * Новой роли и новой группы фича не заводит: ролей в схеме нет намеренно,
   * роль это позиция в паре. Правило ученику окно не показывает, а сервер
   * проверяет то же самое сам: свободное поле у ученика это канал, куда
   * приезжают ПДн школьников, и проверка на клиенте здесь удобство, а не
   * правило.
   *
   * ⚠️ В просмотре кабинета админом окна нет вовсе (раздел 12.6
   * `docs/admin-panel-design.md`): сессия там это преподаватель, и заметка
   * админа легла бы в очередь под его id, а сервер ответил бы 403.
   */
  const teacher = state.status === "signed" && isTeacherSide(state.me) && !state.me.viewer;

  const [widget, setWidget] = useState<Widget | null>(null);
  const [opened, setOpened] = useState<Opened | null>(null);

  /**
   * Поставщики снимка и счётчик живых регистраций на каждый ключ.
   *
   * ⚠️ **Счётчик, а не просто ссылка, и вот зачем.** `StrictMode` монтирует
   * эффекты дважды, а при переходе между экранами React успевает смонтировать
   * новый узел раньше, чем размонтирует старый. В обоих случаях пара
   * `provide(key, fn)` и `provide(key, null)` от разных прогонов способна
   * обнулить СВЕЖЕГО поставщика, и снимок из комнаты приехал бы пустым, причём
   * молча и только в деве или только на быстром переходе. Тот же приём и по той
   * же причине уже стоит в `features/shell/Dialog.tsx` (там счётчик замков
   * прокрутки), а `features/join/JoinPage.tsx` держит для `StrictMode` ref по
   * соседнему поводу.
   */
  const providers = useRef(new Map<string, () => Partial<FeedbackContext>>());
  const alive = useRef(new Map<string, number>());

  const provide = useCallback(
    (key: "room" | "board", fn: (() => Partial<FeedbackContext>) | null) => {
      if (fn !== null) {
        providers.current.set(key, fn);
        alive.current.set(key, (alive.current.get(key) ?? 0) + 1);
        return;
      }
      const left = (alive.current.get(key) ?? 0) - 1;
      alive.current.set(key, Math.max(0, left));
      // Снимаем поставщика, только когда ушёл последний, кто его ставил
      if (left <= 0) providers.current.delete(key);
    },
    [],
  );

  const open = useCallback(() => {
    /**
     * Адрес берётся из `window.location`, а не из `useLocation()`. Подписка на
     * адрес перерисовывала бы провайдер на каждом переходе, а нужен он ровно в
     * этот момент и ровно один раз. Значение то же самое: роутер у нас
     * `BrowserRouter`, других источников адреса нет.
     */
    const pathname = window.location.pathname;
    const extra: Partial<FeedbackContext> = {};
    for (const fn of providers.current.values()) {
      try {
        Object.assign(extra, fn());
      } catch {
        // Поставщик, упавший на разрыве соединения, не отменяет заметку:
        // приоритет у отправки, а снимок это то, что удалось собрать
      }
    }
    setOpened({ snapshot: snapshot(pathname, extra), route: maskRoute(pathname) });
  }, []);

  /**
   * ⚠️ **Ленивый чанк тянется ЗАРАНЕЕ, при первом рендере у преподавателя, а не
   * по нажатию.** Виджет целиком в стартовом чанке недопустим: `AppRoutes`
   * статически импортируется из `main.tsx`, значит всё, на что он ссылается
   * значением, приезжает ученику на 128 кбит/с. Но и качать окно в момент
   * нажатия нельзя: нажимают его как раз тогда, когда сеть не работает. Отсюда
   * третий вариант: чанк заказывается сразу, а до его приезда точки входа нет.
   *
   * Повторная попытка по `online`: первая может не удаться именно потому, что
   * сети не было, и без повтора преподаватель остался бы без окна до
   * перезагрузки вкладки. Очередь при этом работает в любом случае, она в
   * стартовом чанке.
   */
  useEffect(() => {
    if (!teacher || widget !== null) return;
    let live = true;
    const load = (): void => {
      import("./FeedbackWidget.tsx")
        .then((m) => {
          // Функциональная форма обязательна: компонент это функция, и
          // `setWidget(m.FeedbackWidget)` React принял бы за ленивое вычисление
          if (live) setWidget(() => m.FeedbackWidget);
        })
        .catch(() => {
          /* сети нет: повторим по `online` */
        });
    };
    load();
    window.addEventListener("online", load);
    return () => {
      live = false;
      window.removeEventListener("online", load);
    };
  }, [teacher, widget]);

  const close = useCallback(() => setOpened(null), []);

  const enabled = teacher && widget !== null;
  const value = useMemo<FeedbackApi>(() => ({ enabled, open, provide }), [enabled, open, provide]);

  const Widget = widget;
  return (
    <Ctx.Provider value={value}>
      {props.children}
      {/*
        Окно рисуется здесь, а не в точке входа. Во-первых, точек входа четыре, а
        окно одно. Во-вторых, точка входа в комнате живёт внутри меню, которое
        закрывается по нажатию, и окно исчезло бы вместе с ним.

        Перерисовки детей это не стоит: `props.children` приходит тем же
        элементом, и React пропускает поддерево, даже когда провайдер
        перерисовался из-за собственного состояния.
      */}
      {Widget !== null && opened !== null && userId !== null && (
        <Widget
          userId={userId}
          snapshot={opened.snapshot}
          route={opened.route}
          onClose={close}
        />
      )}
    </Ctx.Provider>
  );
}

export function useFeedback(): FeedbackApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useFeedback вне FeedbackProvider");
  return ctx;
}
